import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { validatePassword } from '../../../utils/password.js';
import { authHubAuth } from '../../../config/firebase.js';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { loadRecaptchaScript, getRecaptchaToken } from '../../../utils/recaptcha.js';

/**
 * アカウント設定ページ
 */
const AccountSettings = () => {
  const { user, onRefreshSession } = useOutletContext();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // プロフィール情報
  const [profile, setProfile] = useState({
    displayName: user?.displayName || ''
  });

  useEffect(() => {
    setProfile({ displayName: user?.displayName || '' });
  }, [user?.displayName]);

  // パスワード変更
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // メール通知設定
  const [emailPrefs, setEmailPrefs] = useState({ news: true, promotion: true });
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(true);
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);

  const fetchEmailPrefs = useCallback(async () => {
    try {
      const res = await fetch(buildApiUrl('/api/account/email-preferences'), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmailPrefs(data.emailPreferences || { news: true, promotion: true });
      }
    } catch { /* silent */ } finally {
      setEmailPrefsLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmailPrefs(); }, [fetchEmailPrefs]);

  useEffect(() => {
    loadRecaptchaScript().catch(() => {});
  }, []);

  const handleEmailPrefToggle = async (key) => {
    const newVal = !emailPrefs[key];
    const prev = { ...emailPrefs };
    setEmailPrefs(p => ({ ...p, [key]: newVal }));
    setEmailPrefsSaving(true);
    try {
      const res = await fetch(buildApiUrl('/api/account/email-preferences'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: newVal }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEmailPrefs(data.emailPreferences);
      const msg = newVal ? 'メール通知を有効にしました。' : 'メール通知を無効にしました。';
      setSuccess(msg);
      showToast('account.email-pref.success', { message: msg });
    } catch {
      setEmailPrefs(prev);
      setError('メール通知設定の更新に失敗しました。');
      showToast('account.error', { message: 'メール通知設定の更新に失敗しました。' });
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(buildApiUrl('/api/account/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ displayName: profile.displayName })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'プロフィールの更新に失敗しました。');
      }
      setSuccess('プロフィールを更新しました。');
      showToast('account.profile.success');
      if (typeof onRefreshSession === 'function') {
        onRefreshSession();
      }
    } catch (err) {
      setError(err.message);
      showToast('account.error', { message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('新しいパスワードが一致しません。');
      return;
    }

    const pwdCheck = validatePassword(passwordData.newPassword);
    if (!pwdCheck.valid) {
      setError(pwdCheck.message || 'パスワードは8文字以上・英数字記号で入力してください。');
      return;
    }

    setLoading(true);
    try {
      const currentUser = authHubAuth.currentUser;
      if (!currentUser?.email) {
        setError('認証情報を取得できません。再ログインしてください。');
        return;
      }
      const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      const idToken = await currentUser.getIdToken(true);
      const recaptchaToken = await getRecaptchaToken('change_password');
      const response = await fetch(buildApiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          idToken,
          newPassword: passwordData.newPassword,
          recaptchaToken,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'パスワードの変更に失敗しました。');
      }
      setSuccess('パスワードを変更しました。');
      showToast('account.password.success');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('現在のパスワードが正しくありません。');
        showToast('account.error', { message: '現在のパスワードが正しくありません。' });
      } else {
        setError(err.message || 'パスワードの変更に失敗しました。');
        showToast('account.error', { message: err.message || 'パスワードの変更に失敗しました。' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">アカウント設定</h1>
      </div>

      <div className="hub-main-content">
        {success && (
          <div style={{
            background: 'var(--hub-success-light)',
            color: 'var(--hub-success)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            background: 'var(--hub-danger-light)',
            color: 'var(--hub-danger)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20, overflow: 'visible' }} aria-hidden="true">
              <circle cx="12" cy="12" r="9"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}

        {/* プロフィール */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0" style={{ marginBottom: 'var(--hub-space-xl)' }}>
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-xl)'
          }}>
            プロフィール
          </h2>

          <form onSubmit={handleProfileSubmit}>
            <div style={{ display: 'flex', gap: 'var(--hub-space-xl)', marginBottom: 'var(--hub-space-xl)' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--hub-gradient-primary)',
                borderRadius: 'var(--hub-radius-full)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--hub-text-inverse)',
                fontSize: '2rem',
                fontWeight: 600,
                flexShrink: 0
              }}>
                {profile.displayName?.charAt(0) || user?.userId?.charAt(0) || 'U'}
              </div>
              <div style={{ flex: 1 }}>
                <div className="hub-form-group">
                  <label className="hub-form-label">ユーザーID</label>
                  <input
                    type="text"
                    className="hub-form-input"
                    value={user?.userId || ''}
                    disabled
                    style={{ background: 'var(--hub-bg-tertiary)' }}
                  />
                  <span className="hub-form-hint">ユーザーIDは変更できません</span>
                </div>
              </div>
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">表示名</label>
              <input
                type="text"
                className="hub-form-input"
                value={profile.displayName}
                onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                placeholder="表示名を入力"
              />
            </div>

            <button 
              type="submit" 
              className="hub-btn hub-btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : '保存'}
            </button>
          </form>
        </div>

        {/* パスワード変更 */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-2" data-panel-index="1">
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-xl)'
          }}>
            パスワード変更
          </h2>

          <form onSubmit={handlePasswordSubmit}>
            <div className="hub-form-group">
              <label className="hub-form-label">現在のパスワード</label>
              <input
                type="password"
                className="hub-form-input"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="現在のパスワード"
                required
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">新しいパスワード</label>
              <input
                type="password"
                className="hub-form-input"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="8文字以上・英数字記号"
                required
                minLength={8}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">新しいパスワード（確認）</label>
              <input
                type="password"
                className="hub-form-input"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="もう一度入力"
                required
              />
            </div>

            <button 
              type="submit" 
              className="hub-btn hub-btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'パスワードを変更'}
            </button>
          </form>
        </div>

        {/* メール通知設定 */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-3" data-panel-index="2" style={{ marginTop: 'var(--hub-space-xl)' }}>
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-md)'
          }}>
            メール通知設定
          </h2>
          <p style={{ color: 'var(--hub-text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--hub-space-xl)', lineHeight: 1.6 }}>
            受信するメール通知の種類を設定できます。セキュリティ・決済・規約改定等の重要な通知は設定に関わらず送信されます。
          </p>

          {emailPrefsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-xl)' }}>
              <LoadingSpinner size="small" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-lg)' }}>
              {/* お知らせ */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: 'var(--hub-space-md) var(--hub-space-lg)',
                background: 'var(--hub-bg-secondary)', borderRadius: 'var(--hub-radius-md)',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '2px' }}>お知らせ</div>
                  <div style={{ color: 'var(--hub-text-secondary)', fontSize: '0.8125rem' }}>
                    メンテナンス予告・ライセンス期限通知・支払い方法の更新通知など
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailPrefs.news}
                  disabled={emailPrefsSaving}
                  onClick={() => handleEmailPrefToggle('news')}
                  style={{
                    position: 'relative',
                    width: '48px', height: '26px',
                    borderRadius: '13px', border: 'none', cursor: 'pointer',
                    background: emailPrefs.news
                      ? 'var(--hub-accent-primary, #6366f1)'
                      : 'var(--hub-bg-tertiary, #d1d5db)',
                    transition: 'background 0.2s ease',
                    flexShrink: 0,
                    opacity: emailPrefsSaving ? 0.6 : 1,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '3px',
                    left: emailPrefs.news ? '25px' : '3px',
                    width: '20px', height: '20px',
                    borderRadius: '50%', background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)',
                  }} />
                </button>
              </div>

              {/* プロモーション */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: 'var(--hub-space-md) var(--hub-space-lg)',
                background: 'var(--hub-bg-secondary)', borderRadius: 'var(--hub-radius-md)',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '2px' }}>プロモーション</div>
                  <div style={{ color: 'var(--hub-text-secondary)', fontSize: '0.8125rem' }}>
                    キャンペーン情報・新機能のご紹介・特別オファーなど
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailPrefs.promotion}
                  disabled={emailPrefsSaving}
                  onClick={() => handleEmailPrefToggle('promotion')}
                  style={{
                    position: 'relative',
                    width: '48px', height: '26px',
                    borderRadius: '13px', border: 'none', cursor: 'pointer',
                    background: emailPrefs.promotion
                      ? 'var(--hub-accent-primary, #6366f1)'
                      : 'var(--hub-bg-tertiary, #d1d5db)',
                    transition: 'background 0.2s ease',
                    flexShrink: 0,
                    opacity: emailPrefsSaving ? 0.6 : 1,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '3px',
                    left: emailPrefs.promotion ? '25px' : '3px',
                    width: '20px', height: '20px',
                    borderRadius: '50%', background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)',
                  }} />
                </button>
              </div>

              {/* 重要通知の説明 */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)',
                padding: 'var(--hub-space-md) var(--hub-space-lg)',
                background: 'rgba(99, 102, 241, 0.08)', borderRadius: 'var(--hub-radius-md)',
                borderLeft: '3px solid var(--hub-accent-primary, #6366f1)',
              }}>
                <svg
                  viewBox="-1 -1 26 26"
                  fill="none"
                  stroke="var(--hub-accent-primary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 18, height: 18, flexShrink: 0, marginTop: '2px', overflow: 'visible' }}
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-secondary)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--hub-text-primary)' }}>重要な通知</strong>（パスワード変更、決済関連、ライセンス失効、規約改定等）は、上記の設定に関わらず常に送信されます。
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountSettings;

